// src/components/ScrollToTop.tsx
//
// Every navigation to a new page starts at the top of that page.
//
// ⚠ WHY THIS HAS TO EXIST AT ALL, because "the browser does this" is the wrong
// intuition and it is why the defect survived twenty-seven waves. A full page load
// starts at the top; a CLIENT-SIDE route change does not. React Router swaps the
// route element into the same document and never touches the scroll offset, so each
// page opens at whatever offset the PREVIOUS page was left at.
//
// Reported by the owner on 2026-08-27, from the browser: scroll two-thirds down
// `/bills`, open a bill, and `/bills/DEMO-MINDEF-004897` renders scrolled two-thirds
// down — the breadcrumb, the reference, the APPROVED badge and the whole pro-ration
// split above the fold and invisible. The officer lands mid-payment-history on a page
// whose first job is to say which bill this is. And the landing position is not even
// consistently wrong: it is a function of how far the reader had scrolled the screen
// they came FROM, so the same link opens somewhere different every time.
//
// ⚠ AT THE ROUTER, NOT IN THE PAGES. Thirty-nine routes are registered in `App.tsx`.
// A `useEffect` per page is thirty-nine chances to forget, and the ones that get
// forgotten are reliably the pages nobody thought were long enough to matter — the
// same failure shape as a table shipped without sortable headers.
//
// Three deliberate choices, each of which the obvious version gets wrong:
//
//   · `useLayoutEffect`, not `useEffect`. The layout effect runs BEFORE the browser
//     paints, so the new page is never drawn at the old offset. With `useEffect` the
//     wrong position is painted for one frame and the reader sees the page jump.
//
//   · `pathname` only. `search` deliberately does NOT scroll: a filter, a sort or a
//     pager on a list is the officer operating THIS page, and yanking them to the top
//     on every column sort would be its own defect. Changing which page they are on is
//     what resets the view.
//
//   · A `hash` wins. `/help#pro-ration` is a link that asked for a specific place on
//     the page; forcing it to the top would break in-page anchors and the skip link
//     both.
//
// The scroll is instant. `behavior: 'smooth'` over a page as long as the bill detail
// takes seconds, during which the officer is reading a moving screen.
import { useLayoutEffect } from 'react'
import { useLocation } from 'react-router'

export function ScrollToTop(): null {
  const { pathname, hash } = useLocation()

  useLayoutEffect(() => {
    if (hash !== '') return
    // `scrollTo(0, 0)` rather than `scrollTop = 0` on one element: the console scrolls
    // the window — the rail is `position: sticky` inside the normal flow and nothing in
    // the shell is an `overflow-y: auto` container — so this is the offset that moves.
    window.scrollTo(0, 0)
  }, [pathname, hash])

  return null
}
